import { DatePipe } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from "@angular/core";
import { Chart, ChartConfiguration } from "chart.js";
import { ChartType } from "chart.js/auto";
import zoomPlugin from "chartjs-plugin-zoom";
import { Subject } from "rxjs";
import {
    IHGameStandardize,
    IHSelectedMetric,
    NumericType,
    ParticipantObj,
    TimeType,
} from "src/app/gamification/command-center/models/games-parameter.model";
import {
    CHART_COLORS,
    GAME_COLORS,
    GameType,
    IntervalType,
} from "src/app/gamification/command-center/models/games.model";
import {
    GameHistoryHealthReportService,
    Interval,
} from "src/app/gamification/command-center/services/game-history-health-report.service";
import { GamesParameterHistoryService } from "src/app/gamification/command-center/services/games-parameter-history.service";
import { GamesService } from "src/app/gamification/command-center/services/games.service";
import { GenericMetricFormatterPipe } from "src/app/reusables/pipes/generic-metric-formatter.pipe";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";

Chart.register(zoomPlugin);

@Component({
    selector: "cv-game-effectiveness",
    templateUrl: "./cv-game-effectiveness.component.html",
    styleUrls: ["./cv-game-effectiveness.component.scss"],
})
export class CvGameEffectivenessComponent
    implements OnInit, OnChanges, OnDestroy
{
    @Input() game: any = null;
    @Input() participants: ParticipantObj[] = [];
    @Input() intervalSelected: Interval[] = [];
    @Input() endDate: string = null;

    public chartOptions: ChartConfiguration["options"];

    public chartType: ChartType = "line";
    public chartData: ChartConfiguration["data"] = this.getInitialChartData();

    public numericType: NumericType = NumericType.Number;
    public timeType: TimeType = TimeType.None;
    intervalAverages = [];
    gameTypeName: string;
    gameIntervalType: string;
    gamePeriod: string;
    previousPeriod: string;
    gamePeriodDates: string[] = [];
    previousPeriodDates: string[] = [];
    formattedGamePeriodAvg: string = "---";
    noVariationText: string = "";
    formattedPreviousPeriodAvg: string = "---";
    previousPeriodAvg: number = 0;
    gamePeriodAvg: number = 0;

    historyGame: IHGameStandardize = null;
    _loadingGameParams = true;
    metricList: IHSelectedMetric[] = [];
    gameMetric: IHSelectedMetric = null;
    qualifier: IHSelectedMetric = null;
    gameEffectiveness: any[] = [];
    metricResults: any[] = [];
    previousMetricResults: any[] = [];
    currentMetricResults: any[] = [];
    allMetrics: any[] = [];
    isLoading = false;

    private averageValuesCache = new Map<
        string,
        { previous: any[]; current: any[] }
    >();
    destroy$ = new Subject<void>();

    constructor(
        private gamesParameterHistoryService: GamesParameterHistoryService,
        private gamesService: GamesService,
        private metricFormatter: GenericMetricFormatterPipe,
        private datePipe: DatePipe,
        private cdRef: ChangeDetectorRef,
        private gamesHealthReportService: GameHistoryHealthReportService,
        private cvLocalizationService: CVLocalizationService
    ) {}

    ngOnInit(): void {
        this.initializeComponentState();
        this.initializeChartData();
        this.initializeChartOptions();
        this.noVariationText = `(${this.cvLocalizationService.getCachedText("Admin", "No_Variation", "No variation")})`;
        this.loadGameData();
    }

    private initializeChartOptions(): void {
        const isTimeMetric = this.numericType === NumericType.Time;
        this.chartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            animation: {
                duration: 0,
            },
            plugins: {
                legend: {
                    display: true,
                    labels: {
                        filter: (legendItem, chartData) => {
                            return legendItem.datasetIndex === 0;
                        },
                        color: "#333333",
                        font: {
                            family: "OpenSans",
                            size: 13,
                            weight: 600 as any,
                            lineHeight: "22px",
                        },
                        padding: 10,
                        boxWidth: 15,
                        boxHeight: 15,
                        usePointStyle: false,
                    },
                    onClick: () => {
                        return false;
                    },
                },
                tooltip: {
                    enabled: false,
                    external: this.externalTooltipHandler,
                    callbacks: {
                        label: (context) => {
                            if (context.datasetIndex === 1) {
                                if (
                                    this.game?.GameType === GameType.Brackets &&
                                    this.game.HistoryBracketTournamentRounds
                                        ?.length
                                ) {
                                    const round =
                                        this.game.HistoryBracketTournamentRounds.find(
                                            (r) =>
                                                this.formatPlotDate(
                                                    new Date(r.StartDate)
                                                ) === context.label ||
                                                this.formatPlotDate(
                                                    new Date(r.EndDate)
                                                ) === context.label
                                        );

                                    if (round) {
                                        return this.getBracketRoundName(
                                            round.RoundNumber
                                        );
                                    }
                                    return "";
                                } else if (this.intervalSelected?.[0]) {
                                    return context.label ===
                                        this.formatPlotDate(
                                            new Date(
                                                this.intervalSelected[0].StartDate
                                            )
                                        )
                                        ? this.getLocalizedText(
                                              "Game_Started",
                                              "Game Started"
                                          )
                                        : this.getLocalizedText(
                                              "Game_Ended",
                                              "Game Ended"
                                          );
                                }
                            }
                            if (context.datasetIndex === 0) {
                                return this.formatMetricDisplayValue(
                                    context.parsed.y
                                );
                            }
                            return "";
                        },
                        title: (context) => {
                            return context[0]?.label || "";
                        },
                    },
                },
                datalabels: {
                    display: false,
                },
                zoom: {
                    zoom: {
                        wheel: {
                            enabled: true,
                        },
                        drag: {
                            enabled: false,
                        },
                        pinch: {
                            enabled: true,
                        },
                    },
                    pan: {
                        enabled: true,
                        mode: "xy",
                        modifierKey: null,
                    },
                    limits: {
                        x: { min: "original", max: "original" },
                        y: { min: "original", max: "original" },
                    },
                },
            },
            scales: {
                y: {
                    title: {
                        display: true,
                        text: this.getYAxisLabel(),
                        color: "#767676",
                        font: {
                            family: "OpenSans",
                            size: 8,
                            weight: "bold",
                            lineHeight: "125%",
                        },
                    },
                    ticks: {
                        callback: (value) => {
                            return this.formatMetricDisplayValue(
                                value as number
                            );
                        },
                    },
                    beginAtZero: isTimeMetric,
                },
                x: {
                    beginAtZero: false,
                    ticks: {
                        display: true,
                    },
                },
            },
        };
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes.game && !changes.game.firstChange) {
            this.resetComponent();
        }

        if ((changes.game || changes.historyGame) && this.game) {
            this.chartData = {
                labels: [],
                datasets: [
                    { ...this.chartData.datasets[0], data: [] },
                    { ...this.chartData.datasets[1], data: [] },
                ],
            };
        }

        if (changes.historyGame && this.historyGame) {
            this.gameIntervalType = String(this.historyGame.IntervalType);
            this.updatePeriodsAndChart();
        }

        if (changes.intervalSelected && this.intervalSelected) {
            this.updatePeriodsAndChart();
            this.checkAndLoadMetricRanking();
        }

        if (!this._loadingGameParams) {
            this.getMetrics();
        }

        if (changes.participants && this.participants?.length) {
            this.checkAndLoadMetricRanking();
        }

        if (
            changes.game?.currentValue ||
            changes.intervalSelected?.currentValue ||
            changes.endDate?.currentValue
        ) {
            this.loadGameData().catch(error => {
                console.error('Error loading game data:', error);
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async loadGameData(): Promise<void> {
        if (!this.game || !this.intervalSelected?.[0]) return;

        this._loadingGameParams = true;
        this.isLoading = true;
        try {
            const ruleID = this.game.underlyingRule?.RuleID || this.game.RuleID;
            const endDate =
                this.endDate?.split("T")[0] || this.intervalSelected[0].EndDate?.split("T")[0];

            if (ruleID && endDate) {
                this.historyGame =
                    await this.gamesParameterHistoryService.loadGameHistoryParams(
                        ruleID,
                        endDate
                    );
                this.gameIntervalType = String(this.historyGame.IntervalType);
                this.getMetrics();
                this.updatePeriodsAndChart();
                this.checkAndLoadMetricRanking();
            }
        } catch (error) {
            console.error("Error loading game data:", error);
        } finally {
            this._loadingGameParams = false;
        }
    }

    getLocalizedText(key: string, defaultText: string): string {
        return this.gamesHealthReportService.getLocalizationMessage(
            key,
            defaultText
        );
    }

    checkAndLoadMetricRanking(): void {
        if (
            this.game &&
            this.participants?.length > 0 &&
            this.intervalSelected?.length > 0 &&
            this.gameMetric
        ) {
            this.loadMetricRanking();
        }
    }

    updatePeriodsAndChart(): void {
        if (!this.intervalSelected || this.intervalSelected.length === 0) {
            this.gamePeriod = "---";
            this.previousPeriod = "---";
            this.gamePeriodDates = [];
            this.previousPeriodDates = [];
            return;
        }

        this.updatePreviousPeriod();
        this.updateGamePeriod();
        this.updateChart();
    }

    updateGamePeriod(): void {
        this.gamePeriodDates = this.getGamePeriodDates();
        this.gamePeriod = this.getGamePeriod();
    }

    updatePreviousPeriod(): void {
        this.previousPeriodDates = this.getPreviousPeriodDates();
        this.previousPeriod = this.getPreviousPeriod();
    }

    updateChart(): void {
        if (this.isLoading || !this.chartData) {
            return;
        }

        const gamePeriodDates = this.getGamePeriodDates();
        const previousPeriodDates = this.getPreviousPeriodDates();
        const allDates = [
            ...new Set([...previousPeriodDates, ...gamePeriodDates]),
        ];
        const values = this.chartData.datasets[0].data;

        this.calculatePeriodAverages();

        this.chartData = {
            labels: allDates,
            datasets: [
                {
                    label: this.getChartDatasetLabel(),
                    data: values,
                    fill: "origin",
                    backgroundColor: CHART_COLORS.BackgroundColor,
                    borderColor: CHART_COLORS.BorderColor,
                    tension: 0.1,
                    order: 2,
                    clip: 2,
                },
                {
                    label: "",
                    data: this.getZeroPositionPoints(allDates),
                    pointRadius: 5,
                    pointHoverRadius: 8,
                    showLine: false,
                    order: 1,
                    fill: "origin",
                    pointBackgroundColor: this.getGameColorsValues(),
                    pointBorderColor: this.getGameColorsValues(),
                    tension: 0.1,
                    clip: 5,
                },
            ],
        };
    }

    getZeroPositionPoints(dates: string[]): (number | null)[] {
        const points = new Array(dates.length).fill(null);
        const dateToIndexMap = new Map(
            dates.map((date, index) => [date, index])
        );

        if (
            this.game?.GameType === GameType.Brackets &&
            this.game.HistoryBracketTournamentRounds?.length
        ) {
            this.game.HistoryBracketTournamentRounds.forEach((round) => {
                const roundStartDate = this.formatPlotDate(
                    new Date(round.StartDate)
                );
                const startIndex = dateToIndexMap.get(roundStartDate);
                if (startIndex !== undefined) points[startIndex] = 0;
            });
            return points;
        }

        if (!this.intervalSelected?.[0]) return points;

        const startDate = this.formatPlotDate(
            new Date(this.intervalSelected[0].StartDate)
        );
        const endDateObj = new Date(this.intervalSelected[0].EndDate);
        if (
            String(this.historyGame?.IntervalType) !== IntervalType.Day ||
            this.game?.GameType === GameType.Brackets
        ) {
            endDateObj.setDate(endDateObj.getDate() - 1);
        }
        const endDate = this.formatPlotDate(endDateObj);

        const startIndex = dateToIndexMap.get(startDate);
        const endIndex = dateToIndexMap.get(endDate);

        if (startIndex !== undefined) points[startIndex] = 0;
        if (endIndex !== undefined) points[endIndex] = 0;

        return points;
    }

    async loadMetricRanking(): Promise<void> {
        if (
            !this.game ||
            !this.participants?.length ||
            !this.gameMetric ||
            !this.intervalSelected?.length
        ) {
            this.isLoading = false;
            this.chartData.datasets[0].data = [];
            return;
        }

        if (
            !this.intervalSelected[0]?.StartDate ||
            !this.intervalSelected[0]?.EndDate
        ) {
            this.isLoading = false;
            this.chartData.datasets[0].data = [];
            return;
        }

        this.isLoading = true;

        try {
            const previousStartDate = this.getPreviousStartDate();
            const firstPreviousDate = previousStartDate[0];
            const formattedFirstPreviousDate = new Date(firstPreviousDate)
                .toISOString()
                .replace(/\.\d{3}Z$/, "");

            const formattedParticipants =
                this.participants.map(({ participant }) => ({
                    ...participant,
                    GroupID: participant.RelatedGroupID,
                })) || [];

            const metricID = this.gameMetric.MetricDetail.MetricID.toString();

            if (formattedParticipants.length > 0) {
                let endDateToSend = this.intervalSelected[0].EndDate;
                if (
                    String(this.historyGame?.IntervalType) !==
                        IntervalType.Day ||
                    this.game?.GameType === GameType.Brackets
                ) {
                    const adjustedEndDate = new Date(endDateToSend);
                    adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
                    endDateToSend = adjustedEndDate
                        .toISOString()
                        .replace(/\.\d{3}Z$/, "");
                }

                const allInfo = await this.gamesService.LoadMetricRanking(
                    metricID,
                    formattedParticipants,
                    formattedFirstPreviousDate,
                    endDateToSend,
                    true,
                    this.game.RuleID || this.game.underlyingRule?.RuleID
                );

                this.numericType = allInfo.NumericType;
                this.timeType = allInfo.TimeType;
                this.intervalAverages = allInfo.IntervalAverages;

                const values = this.intervalAverages.map(
                    (item) => item.AverageValue
                );
                const gamePeriodDates = this.getGamePeriodDates();
                const previousPeriodDates = this.getPreviousPeriodDates();
                const graphPeriod = [
                    ...new Set([...previousPeriodDates, ...gamePeriodDates]),
                ];
                const labels =
                    graphPeriod.length <= 3
                        ? graphPeriod
                        : graphPeriod.slice(0, -1);

                const adjustedValues =
                    values.length >= labels.length
                        ? values.slice(0, labels.length)
                        : [
                              ...values,
                              ...Array(labels.length - values.length).fill(0),
                          ];

                this.chartData = {
                    labels: labels,
                    datasets: [
                        {
                            label: this.getChartDatasetLabel(),
                            data: adjustedValues,
                            fill: "origin",
                            backgroundColor: CHART_COLORS.BackgroundColor,
                            borderColor: CHART_COLORS.BorderColor,
                            tension: 0.1,
                            order: 2,
                            clip: 2,
                        },
                        {
                            label: "",
                            data: this.getZeroPositionPoints(labels),
                            pointRadius: 5,
                            pointHoverRadius: 8,
                            showLine: false,
                            pointBackgroundColor: this.getGameColorsValues(),
                            pointBorderColor: this.getGameColorsValues(),
                            order: 1,
                            clip: 5,
                        },
                    ],
                };

                this.calculatePeriodAverages();
                this.updateYAxisLabel();
            }
        } catch (error) {
            console.error("Error loading metric ranking:", error);
        } finally {
            this.isLoading = false;
        }
    }

    calculatePeriodAverages(): void {
        this.previousPeriodAvg = this.getPeriodAverage(
            this.previousPeriodDates
        );
        this.gamePeriodAvg = this.getPeriodAverage(this.gamePeriodDates);

        this.formattedPreviousPeriodAvg = this.formatMetricDisplayValue(
            this.previousPeriodAvg
        );
        this.formattedGamePeriodAvg = this.formatMetricDisplayValue(
            this.gamePeriodAvg
        );
    }

    calculateDifference(): string {
        // Handle null/undefined values
        if (this.gamePeriodAvg == null || this.previousPeriodAvg == null) {
            return this.noVariationText;
        }
        
        // Check if gameMetric is available
        if (!this.gameMetric?.MetricDetail) {
            return this.noVariationText;
        }
        
        const difference = this.gamePeriodAvg - this.previousPeriodAvg;
        
        // Use shared tolerance logic
        if (this.isDifferenceWithinTolerance(difference)) {
            return this.noVariationText;
        }
    
        const sign = difference >= 0 ? "+ " : "- ";
        const absoluteDifference = Math.abs(difference);
    
        // Add null check for ConversionType and provide default
        const conversionType = this.gameMetric?.MetricDetail?.ConversionType;
        const displayValue = this.getFormattedData(
            absoluteDifference,
            conversionType // This can now be undefined safely
        );
        return `(${sign}${displayValue})`;
    }

    parseNumericValue(value: string): number {
        if (value === "---") return 0;
        return parseFloat(value.replace(/[^0-9.-]+/g, ""));
    }

    getStyleClass(): string {
        // Handle null/undefined values
        if (this.gamePeriodAvg == null || this.previousPeriodAvg == null) {
            return "metric-change metricChangeZero";
        }
        
        const difference = this.gamePeriodAvg - this.previousPeriodAvg;
        
        // Use shared tolerance logic
        if (this.isDifferenceWithinTolerance(difference)) {
            return "metric-change metricChangeZero";
        }

        const isMetricChangePositive =
            (this.metricDirection === "Up" && difference > 0) ||
            (this.metricDirection !== "Up" && difference < 0);
        
        return isMetricChangePositive
            ? "metric-change metricChangePositive"
            : "metric-change metricChangeNegative";
    }

    formatMetricDisplayValue(value: number): string {
        if (value === null || value === undefined) {
            return "";
        }

        switch (this.numericType) {
            case NumericType.Number:
                return this.formatNumber(value);
            case NumericType.Currency:
                return this.formatCurrency(value);
            case NumericType.Percent:
                return this.formatPercent(value);
            case NumericType.Time:
                if (this.timeType === TimeType.Time) {
                    return this.formatMetricTime(value);
                } else {
                    return this.formatNumber(value);
                }
            default:
                return this.formatNumber(value);
        }
    }

    formatNumber(value: number): string {
        return value.toLocaleString(typeof CVCultureName !== 'undefined' ? CVCultureName : 'en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3,
        });
    }

    formatCurrency(value: number): string {
        return value.toLocaleString(typeof CVCultureName !== 'undefined' ? CVCultureName : 'en-US' , {
            style: "currency",
            currency: typeof CVISOCurrencySymbol !== 'undefined' ? CVISOCurrencySymbol : 'USD',
            maximumFractionDigits: 2,
        });
    }

    formatPercent(value: number): string {
        return value.toLocaleString(typeof CVCultureName !== 'undefined' ? CVCultureName : 'en-US', {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    formatMetricTime(seconds: number): string {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        return `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
    }

    getPeriodAverage(dates: string[]): number {
        if (!this.intervalAverages || dates.length === 0) {
            return 0;
        }

        const formattedIntervalAverages = this.intervalAverages.map((item) => {
            const [year, month, day] = item.Date.split("T")[0].split("-");
            const date = new Date(Number(year), Number(month) - 1, Number(day));
            return {
                ...item,
                Date: this.datePipe.transform(date, "MM/dd") || "",
            };
        });

        const valuesInPeriod = formattedIntervalAverages
            .filter((item) => dates.includes(item.Date))
            .map((item) => item.AverageValue);

        if (valuesInPeriod.length === 0) {
            return 0;
        }

        const sum = valuesInPeriod.reduce((acc, value) => acc + value, 0);
        return sum / valuesInPeriod.length;
    }

    getChartDatasetLabel(): string {
        return this.gameMetric && this.gameMetric.MetricDetail?.Abbrev;
    }

    onMetricSelectionChange(): void {
        if (this.gameMetric?.MetricDetail?.MetricID) {
            this.gamesParameterHistoryService.lastSelectedMetricId = this.gameMetric.MetricDetail.MetricID;
        }

        if (this.game?.GameType == GameType.Connect3 || this.game?.GameType == GameType.Blackout) {
            this.gamesParameterHistoryService.currentConnect3 = this.gameMetric;
        }
        this.calculatePeriodAverages();
        this.updateChartDatasetLabel();
        this.loadMetricRanking();
    }

    updateChartDatasetLabel(): void {
        if (this.chartData?.datasets?.length > 0) {
            this.chartData.datasets[0].label = this.getChartDatasetLabel();
            this.chartData = { ...this.chartData };
        }
    }

    getGamePeriod(): string {
        if (
            !this.intervalSelected[0]?.StartDate ||
            !this.intervalSelected[0]?.EndDate
        ) {
            return "---";
        }

        const startDate = new Date(this.intervalSelected[0].StartDate);
        const endDate = new Date(this.intervalSelected[0].EndDate);

        return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
    }

    getMetrics(): void {
        if (
            !this.intervalSelected ||
            this.intervalSelected.length === 0 ||
            !this.intervalSelected[0]?.EndDate
        ) {
            return;
        }
    
        this.metricList = [];
        const ruleId = this.game.RuleID || this.game.underlyingRule?.RuleID;
        const endDate = this.intervalSelected[0].EndDate?.split("T")[0];
        const metrics = this.gamesParameterHistoryService.getMetrics(ruleId, endDate);
        
        if (!metrics || metrics.length === 0) {
            return;
        }
        
        const formattedMetricList = this.transformMetricList(metrics);
        
        const previousMetricId = this.gamesParameterHistoryService.lastSelectedMetricId || 
                                this.gameMetric?.MetricDetail?.MetricID;
        let previousMetricFound = false;
    
        if (
            previousMetricId && 
            this.gamesParameterHistoryService.lastRuleId === ruleId
        ) {
            const previousMetric = formattedMetricList.find(
                m => m.MetricDetail?.MetricID === previousMetricId && 
                     !m.IsQualifier
            );
            
            if (previousMetric) {
                this.gameMetric = previousMetric;
                previousMetricFound = true;
                
                this.gamesParameterHistoryService.lastSelectedMetricId = previousMetricId;
            }
        }
    
        if (!previousMetricFound) {
            if (this.game?.GameType == GameType.Raffle) {
                this.gameMetric = formattedMetricList.length > 0 ? formattedMetricList[0] : null;
            } else if (
                this.game?.GameType != GameType.Connect3 &&
                this.game?.GameType != GameType.Blackout
            ) {
                this.gameMetric =
                    formattedMetricList.length > 0 ? formattedMetricList.find((x) => !x.IsQualifier) : null;
            } else {
                this.metricList = formattedMetricList.filter((x) => !x.IsQualifier);
                this.gameMetric = this.metricList[0];
                
                if (this.gameMetric) {
                    this.gamesParameterHistoryService.currentConnect3 = this.gameMetric;
                }
            }
            
            if (this.gameMetric?.MetricDetail?.MetricID) {
                this.gamesParameterHistoryService.lastSelectedMetricId = this.gameMetric.MetricDetail.MetricID;
            }
        }
    
        this.metricList = formattedMetricList.filter((x) => !x.IsQualifier).filter((item, index, self) =>
            index === self.findIndex((m) => m.MetricDetail?.MetricID === item.MetricDetail?.MetricID)
        );
        
        this.gamesParameterHistoryService.lastRuleId = ruleId;
    }

    getPreviousPeriod(): string {
        if (
            !this.intervalSelected[0]?.StartDate ||
            !this.intervalSelected[0]?.EndDate
        ) {
            return "---";
        }

        const { startDate, endDate } = this.calculatePreviousPeriodDates();
        return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
    }

    getGamePeriodDates(): string[] {
        if (
            !this.intervalSelected[0]?.StartDate ||
            !this.intervalSelected[0]?.EndDate
        ) {
            return [];
        }

        const startDate = new Date(this.intervalSelected[0].StartDate);
        const endDate = new Date(this.intervalSelected[0].EndDate);

        return this.getDatesInRangeValues(startDate, endDate);
    }

    getPreviousPeriodDates(): string[] {
        if (
            !this.intervalSelected[0]?.StartDate ||
            !this.intervalSelected[0]?.EndDate
        ) {
            return [];
        }

        const { startDate, endDate } = this.calculatePreviousPeriodDates();
        return this.getDatesInRangeValues(startDate, endDate);
    }

    getPreviousStartDate(): Date[] {
        if (
            !this.intervalSelected[0]?.StartDate ||
            !this.intervalSelected[0]?.EndDate
        ) {
            return [];
        }

        const { startDate } = this.calculatePreviousPeriodDates();
        return [startDate];
    }

    calculatePreviousPeriodDates(): { startDate: Date; endDate: Date } {
        if (
            !this.intervalSelected ||
            !this.intervalSelected.length ||
            !this.intervalSelected[0]?.StartDate ||
            !this.intervalSelected[0]?.EndDate
        ) {
            return { startDate: new Date(), endDate: new Date() };
        }

        const startDate = new Date(this.intervalSelected[0].StartDate);
        let endDate = new Date(this.intervalSelected[0].EndDate);

        if (this.game?.GameType == GameType.Brackets) {
            startDate?.setDate(startDate?.getDate() - 7);
            endDate = new Date(startDate);
            endDate.setDate(endDate.getDate() + 6);
            return { startDate, endDate };
        }

        switch (String(this.gameIntervalType)) {
            case IntervalType.Day:
                startDate?.setDate(startDate?.getDate() - 1);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 1);
                break;
            case IntervalType.Week:
                startDate?.setDate(startDate?.getDate() - 7);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 6);
                break;
            case IntervalType.Month:
                startDate?.setDate(startDate?.getDate() - 14);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 13);
                break;
            default:
                break;
        }

        return { startDate, endDate };
    }

    getGameColorsValues(): string {
        if (!this.game) return GAME_COLORS.Default;
        switch (this.game.GameType) {
            case GameType.MultiplierMadness:
                return GAME_COLORS.MultiplierMadness;
            case GameType.TopDog:
                return GAME_COLORS.TopDog;
            case GameType.Bullseye:
                return GAME_COLORS.Bullseye;
            case GameType.Connect3:
                return GAME_COLORS.Connect3;
            case GameType.Milestone:
                return GAME_COLORS.Milestone;
            case GameType.TopTeam:
                return GAME_COLORS.TopTeam;
            case GameType.Brackets:
                return GAME_COLORS.Brackets;
            case GameType.Raffle:
                return GAME_COLORS.Raffle;
            case GameType.Blackout:
                return GAME_COLORS.ConnectAll;
            default:
                return GAME_COLORS.Default;
        }
    }

    getDatesInRangeValues(startDate: Date, endDate: Date): string[] {
        const datesArray: string[] = [];
        let currentDateValue = new Date(startDate);

        while (currentDateValue <= endDate) {
            datesArray.push(this.formatPlotDate(currentDateValue));
            currentDateValue.setDate(currentDateValue.getDate() + 1);
        }

        return datesArray;
    }

    getBracketRoundName(round: number): string {
        switch (round) {
            case 1:
                return this.getLocalizedText("Round_1_Final", "Final");
            case 2:
                return this.getLocalizedText(
                    "Round_2_Semi_Finals",
                    "Semi Finals"
                );
            case 3:
                return this.getLocalizedText("Round_3_Quarter", "Quarters");
            case 4:
                return this.getLocalizedText("Round_4_Octaves", "Octaves");
            default:
                return "";
        }
    }

    formatDate(date: Date): string {
        return this.datePipe.transform(date, "MM/dd/yyyy","", CVCultureName) || "";
    }

    formatPlotDate(date: Date): string {
        return this.datePipe.transform(date, "MM/dd") || "";
    }

    get metricDirection(): string {
        if (!this.gameMetric || !this.gameMetric.MetricDetail) {
            return this.getLocalizedText("Down", "Down");
        }
        const direction = Number(
            this.gameMetric.MetricDetail.DefaultMetricDirection
        );
        return direction === 1
            ? this.getLocalizedText("Up", "Up")
            : this.getLocalizedText("Down", "Down");
    }

    private getFormattedData(value: any, conversionType: any): string {
        const conversionTypeString = conversionType?.toString() || '';
        return this.metricFormatter.transform(value, conversionTypeString);
    }

    private getAverageValuesCacheKey(
        ruleId: number,
        displayInterval: string
    ): string {
        return `AverageValuesCache_${ruleId}_${displayInterval}`;
    }

    get isDataLoaded(): boolean {
        return !this._loadingGameParams && !!this.historyGame;
    }

    get isLoadingGameParams(): boolean {
        return this._loadingGameParams;
    }

    getOrCreateTooltip = (chart) => {
        let tooltipEl =
            chart.canvas.parentNode.querySelector(".custom-tooltip");

        if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.className = "custom-tooltip";
            tooltipEl.style.background = "rgba(255, 255, 255, 0.95)";
            tooltipEl.style.border = "1px solid #E0E0E0";
            tooltipEl.style.borderRadius = "8px";
            tooltipEl.style.color = "#000000CC";
            tooltipEl.style.opacity = "1";
            tooltipEl.style.pointerEvents = "none";
            tooltipEl.style.position = "absolute";
            tooltipEl.style.transform = "translate(-50%, -100%)";
            tooltipEl.style.transition = "all .1s ease";
            tooltipEl.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
            tooltipEl.style.padding = "8px 12px";
            tooltipEl.style.fontFamily = "OpenSans, Arial, sans-serif";
            tooltipEl.style.fontSize = "12px";
            tooltipEl.style.fontWeight = "400";
            tooltipEl.style.lineHeight = "125%";
            tooltipEl.style.zIndex = "1000";

            const table = document.createElement("table");
            table.style.margin = "0px";
            table.style.borderSpacing = "0";
            table.style.borderCollapse = "collapse";

            tooltipEl.appendChild(table);
            chart.canvas.parentNode.appendChild(tooltipEl);
        }

        return tooltipEl;
    };

    externalTooltipHandler = (context) => {
        // Tooltip Element
        const { chart, tooltip } = context;
        const tooltipEl = this.getOrCreateTooltip(chart);

        // Hide if no tooltip
        if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
        }

        // Set Text
        if (tooltip.body) {
            const titleLines = tooltip.title || [];
            const bodyLines = tooltip.body.map((b) => b.lines);

            const isSpecialPoint = tooltip.dataPoints[0]?.datasetIndex === 1;

            const tableBody = document.createElement("tbody");
            const createTd = () => {
                const td = document.createElement("td");
                td.style.borderWidth = "0";
                td.style.padding = "4px 0";
                td.style.margin = "0";
                td.style.fontFamily = "OpenSans, Arial, sans-serif";
                td.style.fontSize = "12px";
                td.style.fontWeight = "400";
                td.style.lineHeight = "125%";
                td.style.color = "#000000CC";
                td.style.textAlign = "center";
                td.style.whiteSpace = "nowrap";
                return td;
            };
            if (
                isSpecialPoint &&
                titleLines.length > 0 &&
                bodyLines.length > 0
            ) {
                const tr = document.createElement("tr");
                tr.style.borderWidth = "0";

                const td = createTd();

                const combinedText = `${titleLines[0]} - ${bodyLines[0][0]}`;
                const text = document.createTextNode(combinedText);
                td.appendChild(text);

                tr.appendChild(td);
                tableBody.appendChild(tr);
            } else {
                const tr = document.createElement("tr");
                tr.style.borderWidth = "0";

                const td = createTd();

                const dateText = document.createTextNode(`${titleLines[0]} - `);
                td.appendChild(dateText);

                if (bodyLines.length > 0 && tooltip.labelColors[0]) {
                    const colors = tooltip.labelColors[0];
                    const span = document.createElement("span");
                    span.style.background = colors.backgroundColor;
                    span.style.borderColor = colors.borderColor;
                    span.style.borderWidth = "1px";
                    span.style.borderStyle = "solid";
                    span.style.marginRight = "6px";
                    span.style.marginLeft = "6px";
                    span.style.height = "8px";
                    span.style.width = "8px";
                    span.style.display = "inline-block";
                    span.style.borderRadius = "2px";
                    span.style.flexShrink = "0";
                    td.appendChild(span);
                }

                const valueText = document.createTextNode(
                    bodyLines[0][0] || ""
                );
                td.appendChild(valueText);

                tr.appendChild(td);
                tableBody.appendChild(tr);
            }

            const tableRoot = tooltipEl.querySelector("table");

            // Remove old children
            while (tableRoot.firstChild) {
                tableRoot.firstChild.remove();
            }

            // Add new children
            tableRoot.appendChild(tableBody);
        }

        const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

        tooltipEl.style.opacity = "1";
        tooltipEl.style.left = positionX + tooltip.caretX + "px";
        tooltipEl.style.top = positionY + tooltip.caretY - 10 + "px";
    };

    getYAxisLabel(): string {
        switch (this.numericType) {
            case NumericType.Number:
                return this.cvLocalizationService.getCachedText("Admin", "Number", "Number");
            case NumericType.Currency:
                return this.cvLocalizationService.getCachedText("Admin", "Currency", "Currency");
            case NumericType.Percent:
                return this.cvLocalizationService.getCachedText("Admin", "Percent", "Percent");
            case NumericType.Time:
                if (this.timeType === TimeType.Time) {
                    return this.cvLocalizationService.getCachedText("Admin", "Time", "Time");
                } else {
                    return this.cvLocalizationService.getCachedText("Admin", "Duration", "Duration");
                }
            default:
                return this.cvLocalizationService.getCachedText("Admin", "Number", "Number");
        }
    }

    updateYAxisLabel(): void {
        const yScale = this.chartOptions?.scales?.y as any;
        const isTimeMetric = this.numericType === NumericType.Time;

        if (yScale?.title) {
            yScale.title.text = this.getYAxisLabel();
            this.chartOptions = { ...this.chartOptions };
        }

        if (isTimeMetric) {
            yScale.beginAtZero = true;
        } else {
            yScale.beginAtZero = false;
        }
    }

    /**
     * Calculate tolerance value based on numeric type and values
     */
    private calculateTolerance(): number {
        switch (this.numericType) {
            case NumericType.Percent:
                return 0.001;
            case NumericType.Currency:
                return 0.001;
            case NumericType.Time:
                return 0;
            case NumericType.Number:
            default:
                // For numbers, use relative tolerance to prevent division by zero
                const avgValue = (Math.abs(this.gamePeriodAvg) + Math.abs(this.previousPeriodAvg)) / 2;
                // Use minimum tolerance of 0.01 to handle very small numbers
                return Math.max(avgValue * 0.001, 0.01); // 0.1% of average value, minimum 0.01
        }
    }

    /**
     * Check if difference is within tolerance (essentially zero)
     */
    private isDifferenceWithinTolerance(difference: number): boolean {
        if (isNaN(difference) || !isFinite(difference)) {
            return true; // Treat invalid differences as no variation
        }
        
        const tolerance = this.calculateTolerance();
        return Math.abs(difference) <= tolerance;
    }

    private resetComponent(): void {
        this.isLoading = false;
        this._loadingGameParams = true;
        
        this.historyGame = null;
        this.gameTypeName = undefined;
        this.gameIntervalType = undefined;
        
        this.gamePeriod = "---";
        this.previousPeriod = "---";
        this.gamePeriodDates = [];
        this.previousPeriodDates = [];
        
        this.formattedGamePeriodAvg = "---";
        this.formattedPreviousPeriodAvg = "---";
        this.previousPeriodAvg = 0;
        this.gamePeriodAvg = 0;
        
        this.metricList = [];
        this.gameMetric = null;
        this.qualifier = null;
        this.gameEffectiveness = [];
        this.metricResults = [];
        this.previousMetricResults = [];
        this.currentMetricResults = [];
        this.allMetrics = [];
        
        this.intervalAverages = [];
        this.numericType = NumericType.Number;
        this.timeType = TimeType.None;
        
        this.averageValuesCache.clear();
        
        this.initializeChartData();
        
        this.initializeChartOptions();
        
        this.cdRef.detectChanges();
    }

    private initializeChartData(): void {
        this.chartData = this.getInitialChartData();
    }

    private initializeComponentState(): void {
        this.gamePeriod = "---";
        this.previousPeriod = "---";
        this.gamePeriodDates = [];
        this.previousPeriodDates = [];
        this.formattedGamePeriodAvg = "---";
        this.formattedPreviousPeriodAvg = "---";
        this.previousPeriodAvg = 0;
        this.gamePeriodAvg = 0;
        this.numericType = NumericType.Number;
        this.timeType = TimeType.None;
    }

    private getInitialChartData(): ChartConfiguration["data"] {
        return {
            labels: [],
            datasets: [
                {
                    label: "",
                    data: [],
                    fill: "origin",
                    backgroundColor: CHART_COLORS.BackgroundColor,
                    borderColor: CHART_COLORS.BorderColor,
                    tension: 0.1,
                    order: 2,
                    clip: 2,
                },
                {
                    label: "",
                    data: [],
                    pointRadius: 5,
                    pointHoverRadius: 8,
                    showLine: false,
                    fill: "origin",
                    pointBackgroundColor: this.getGameColorsValues(),
                    pointBorderColor: this.getGameColorsValues(),
                    tension: 0.1,
                    order: 1,
                    clip: 5,
                    datalabels: {
                        display: false,
                    },
                },
            ],
        };
    }
    
    transformMetricList(metrics: IHSelectedMetric[]) {
        return metrics
            .map((metric) => ({
                ...metric,
                displayLabel: metric.MetricDetail?.Abbrev || "",
            }))
            .sort((a, b) => a.Position - b.Position);
    }
}

